import { flash, SubmitButton, Switch } from "@xola/ui-kit";
import { FormField, requiredNumberMoreThanZero, requiredString, TextareaField } from "common";
import { omit } from "lodash";
import React, { useEffect, useState } from "react";
import { useMutation } from "react-query";
import { useHistory } from "react-router-dom";
import {
    CouponAlgorithm,
    CouponScope,
    LineItemCode,
    Membership,
    PurchaseRule,
    PurchaseRuleAction,
    PurchaseRuleActionObject,
    PurchaseRuleFilterObject,
    PurchaseRuleShortcut,
    PurchaseRuleShortcutDiscountOption,
    PurchaseRuleShortcutType,
    SelectionState,
} from "types";
import { array, boolean, number, object, string } from "yup";
import { api } from "@/api/api";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { XolaFormik } from "@/components/Common/XolaFormik";
import { ContentLayout } from "@/components/Layout/ContentLayout";
import { MembershipBenefitsDiscountForm } from "@/components/Product/Membership/MembershipBenefits/MembershipBenefitsDiscountForm";
import { useSeller } from "@/hooks/useAccount";
import { useInvalidate } from "@/hooks/useInvalidation";

export interface MembershipDiscountBenefits {
    enableDiscount: boolean;
    amount: number;
    algorithm: CouponAlgorithm;
    scope: CouponScope;
    demographics: SelectionState;
    demographicsLimit: number;
}

export interface MembershipBenefitsFormData extends MembershipDiscountBenefits {
    additionalBenefits: string;
}

interface MembershipBenefitsProps {
    membership: Membership;
}

const defaultValues: MembershipBenefitsFormData = {
    enableDiscount: false,
    amount: 0,
    algorithm: CouponAlgorithm.ABSOLUTE,
    scope: CouponScope.PER_BOOKING,
    demographics: { all: true, selected: [] },
    demographicsLimit: 0,
    additionalBenefits: "",
};

export const MembershipBenefits = ({ membership }: MembershipBenefitsProps) => {
    const seller = useSeller();
    const invalidate = useInvalidate();
    const history = useHistory();

    const [didCreateZeroDiscount, setDidCreateZeroDiscount] = useState(false);

    const { mutateAsync: saveMembership } = useMutation(async (payload: Partial<Membership>) => {
        return api.put<Membership>(`/memberships/${membership.id}`, payload);
    });

    const { mutateAsync: savePurchaseRule } = useMutation(async (payload: PurchaseRule) => {
        return api.put(`/purchaseRules/${membership.redemptionPurchaseRule!.id}`, payload);
    });

    const invalidateData = () => {
        invalidate("products");
        invalidate("memberships", membership.id);
        invalidate("purchaseRuleLinks", membership.redemptionPurchaseRuleLink?.id);
        invalidate("purchaseRules", membership.redemptionPurchaseRule?.id);
    };

    const handleSubmit = async (values: MembershipBenefitsFormData) => {
        if (membership.additionalBenefits !== values.additionalBenefits) {
            await saveMembership({
                id: membership.id,
                additionalBenefits: values.additionalBenefits,
                seller: { id: seller.id },
            });
        }

        const actions = getPurchaseRuleActions(membership, values);
        await savePurchaseRule({
            ...membership.redemptionPurchaseRule,
            actions,
        });

        invalidateData();

        flash.success("Successfully updated membership benefits");

        if (!membership.redemptionPurchaseRule?.filter?.filters?.length) {
            history.push(`/products/memberships/${membership.id}/restrictions`);
        }
    };

    const createZeroDiscountAction = async () => {
        const actions = getZeroDiscountActions(membership);
        await savePurchaseRule({
            ...membership.redemptionPurchaseRule,
            actions,
        });
        invalidateData();
    };

    useEffect(() => {
        // Only run once per mount (or when the membership changes).
        if (
            !didCreateZeroDiscount &&
            membership?.redemptionPurchaseRule?.id &&
            (!membership.redemptionPurchaseRule.actions || membership.redemptionPurchaseRule.actions.length === 0)
        ) {
            setDidCreateZeroDiscount(true);
            createZeroDiscountAction();
        }
    }, [didCreateZeroDiscount, membership?.redemptionPurchaseRule?.id, membership?.redemptionPurchaseRule?.actions]);

    return (
        <ContentLayout isWide>
            <XolaFormik<MembershipBenefitsFormData>
                enablePrompt
                enableReinitialize
                validateOnChange
                validateOnBlur
                validationSchema={validationSchema}
                initialValues={getInitialValues(membership)}
                onSubmit={handleSubmit}
            >
                {({ values, setFieldValue, isSubmitting, dirty, handleSubmit, handleReset }) => {
                    const handleEnabledToggle = (checked: boolean) => {
                        setFieldValue("enableDiscount", checked);
                    };

                    return (
                        <>
                            <ContentLayout.Section title="Benefits">
                                <div className="flex flex-col">
                                    <FormField name="enableDiscount">
                                        <Switch.Group className="!items-start">
                                            <Switch
                                                size="medium"
                                                isChecked={values.enableDiscount}
                                                onChange={handleEnabledToggle}
                                            />
                                            <Switch.Label direction="right" className="flex flex-col">
                                                <span className="font-bold">Discount</span>
                                                <span>
                                                    Offer a discount when this membership pass is used in a booking
                                                </span>
                                            </Switch.Label>
                                        </Switch.Group>
                                    </FormField>

                                    {values.enableDiscount ? <MembershipBenefitsDiscountForm /> : null}

                                    <FormField name="additionalBenefits" label="Additional Benefits">
                                        <TextareaField rows={4} />
                                    </FormField>
                                </div>
                            </ContentLayout.Section>

                            <ContentLayout.Footer preferenceSidebar>
                                <SubmitButton isLoading={isSubmitting} disabled={!dirty} onClick={handleSubmit}>
                                    Save
                                </SubmitButton>

                                <SecondaryButton type="reset" disabled={!dirty} onClick={handleReset}>
                                    Cancel
                                </SecondaryButton>
                            </ContentLayout.Footer>
                        </>
                    );
                }}
            </XolaFormik>
        </ContentLayout>
    );
};

const validationSchema = object().shape({
    enableDiscount: boolean(),
    amount: number().when("enableDiscount", {
        is: true,
        then: number().when("algorithm", {
            is: CouponAlgorithm.ABSOLUTE,
            then: requiredNumberMoreThanZero(),
            otherwise: requiredNumberMoreThanZero().lessThan(101, "cannot exceed 100 for percentage discount"),
        }),
    }),
    algorithm: requiredString().oneOf([CouponAlgorithm.ABSOLUTE, CouponAlgorithm.PERCENT]),
    scope: requiredString().oneOf([CouponScope.PER_BOOKING, CouponScope.PER_PERSON]),
    demographics: object()
        .shape({
            all: boolean(),
            selected: array().of(string()),
        })
        .required(),
    additionalBenefits: string(),
});

export const getInitialValues = (membership: Membership): MembershipBenefitsFormData => {
    const formValues = { ...defaultValues, additionalBenefits: membership.additionalBenefits ?? "" };

    if (!membership?.redemptionPurchaseRule?.actions?.length) {
        return formValues;
    }

    return {
        ...getMembershipDiscountBenefits(membership.redemptionPurchaseRule),
        additionalBenefits: membership.additionalBenefits ?? "",
    };
};

export const getMembershipDiscountBenefits = (redemptionPurchaseRule: PurchaseRule): MembershipDiscountBenefits => {
    const [action] = redemptionPurchaseRule.actions;
    if (!action?.shortcut) {
        return omit(defaultValues, "additionalBenefits");
    }

    const { shortcut } = action;
    const values: MembershipDiscountBenefits = {
        enableDiscount: true,
        amount: Math.abs(shortcut.amount),
        algorithm:
            shortcut.type === PurchaseRuleShortcutType.PERCENT ? CouponAlgorithm.PERCENT : CouponAlgorithm.ABSOLUTE,
        scope: CouponScope.PER_BOOKING,
        demographics: { all: true, selected: [] },
        demographicsLimit: shortcut.options?.maxUnitCount ?? 0,
    };

    if (shortcut.options?.templateIds) {
        values.scope = CouponScope.PER_PERSON;
        values.demographics = { all: false, selected: shortcut.options.templateIds };
    }

    if (shortcut.options?.templateCodes) {
        values.scope = CouponScope.PER_PERSON;
        values.demographics = { all: true, selected: [] };
    }

    if (shortcut.amount === 0 && shortcut.type === PurchaseRuleShortcutType.ABSOLUTE_PER_PURCHASE) {
        values.enableDiscount = false;
    }

    return values;
};

const getZeroDiscountActions = (membership: Membership) => {
    let actions: PurchaseRuleAction[] = [];

    if (membership.redemptionPurchaseRule?.actions.length) {
        actions = membership.redemptionPurchaseRule.actions.map((action) => ({
            ...action,
            _remove: true,
        }));
    }

    actions.push(
        getMembershipDiscountLineItemAction({
            type: PurchaseRuleShortcutType.ABSOLUTE_PER_PURCHASE,
            amount: 0,
            options: {},
        }),
    );

    return actions;
};

export const getPurchaseRuleActions = (
    membership: Membership,
    values: MembershipBenefitsFormData,
): PurchaseRuleAction[] => {
    if (!values.enableDiscount) {
        return getZeroDiscountActions(membership);
    }

    const typeMap: Record<CouponAlgorithm, Record<CouponScope, PurchaseRuleShortcutType>> = {
        [CouponAlgorithm.ABSOLUTE]: {
            [CouponScope.PER_PERSON]: PurchaseRuleShortcutType.ABSOLUTE_PER_UNIT,
            [CouponScope.PER_EQUIPMENT]: PurchaseRuleShortcutType.ABSOLUTE_PER_UNIT,
            [CouponScope.PER_BOOKING]: PurchaseRuleShortcutType.ABSOLUTE_PER_PURCHASE,
            [CouponScope.PER_MERCHANDISE]: PurchaseRuleShortcutType.ABSOLUTE_PER_UNIT,
        },
        [CouponAlgorithm.PERCENT]: {
            [CouponScope.PER_PERSON]: PurchaseRuleShortcutType.PERCENT,
            [CouponScope.PER_EQUIPMENT]: PurchaseRuleShortcutType.PERCENT,
            [CouponScope.PER_BOOKING]: PurchaseRuleShortcutType.PERCENT,
            [CouponScope.PER_MERCHANDISE]: PurchaseRuleShortcutType.PERCENT,
        },
    };

    const options: PurchaseRuleShortcutDiscountOption = {};

    if (values.scope === CouponScope.PER_PERSON) {
        if (values.demographics.all || values.demographics.selected.length === 0) {
            options.templateCodes = [LineItemCode.DEMOGRAPHIC];
        } else {
            options.templateIds = values.demographics.selected;
        }

        if (values.demographicsLimit) {
            options.maxUnitCount = values.demographicsLimit;
        }

        options.minUnitCount = 0;
    }

    const type = typeMap[values.algorithm][values.scope] ?? PurchaseRuleShortcutType.PERCENT;
    const [action] = membership.redemptionPurchaseRule?.actions ?? [];

    return [
        getMembershipDiscountLineItemAction(
            {
                type,
                amount: Number(values.amount) * -1,
                options,
            },
            omit(action, "modifiers"),
        ),
    ];
};

const getMembershipDiscountLineItemAction = (
    shortcut: PurchaseRuleShortcut,
    existingAction?: PurchaseRuleAction,
): PurchaseRuleAction => {
    return {
        ...existingAction,
        object: PurchaseRuleActionObject.UPDATE_LINE_ITEM_ACTION,
        filter: {
            object: PurchaseRuleFilterObject.EQUALS_FILTER,
            field: "template.code",
            operand: LineItemCode.MEMBERSHIP_DISCOUNT,
        },
        shortcut,
    };
};
